import classnames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  SuggestInput,
  TextInput,
  CloseIcon as closeIcon,
  GreenCheckIcon as greenCheckIcon,
  NewTabIconGreen as newTabIcon,
} from '@/shared/ui';
import { SUGGEST_COMMENT, TYPE_EVENT_OTHER } from '@/shared/config';
import useDeviceEditStripBox from './useDeviceEditStripBox';

export type DeviceEditStripBoxProps = {
  className?: string;
  keyRecoil: any;
  isActive?: boolean;
};

/**
 * Detail pane for the selected device event strip, showing its identifying fields and the editable title and technician comment. useDeviceEditStripBox drives it: a skeleton stands in while the strip loads and a no-data message shows when nothing is selected, event id, type, trigger time and symptoms appear only for the event kinds that carry them, per-field cancel and apply buttons fade in once a value diverges from the stored one, and inputs lock when the event is removed or has no ECG data.
 *
 * @param props - Pane props including the extra className and the tab key and active flag passed to the hook.
 * @returns The rendered detail pane, its loading skeleton, or the no-data message.
 */
const DeviceEditStripBox = ({ className, keyRecoil, isActive }: DeviceEditStripBoxProps) => {
  const {
    isLoadingStrip,
    selectedData,
    state,
    eventId,
    eventType,
    triggerTime,
    symptoms,
    isNoEcgData,
    isDisabled,
    onClickNavigate,
    onChange,
    onKeyDownInput,
    onClickApplyChange,
    onClickCancelChange,
  } = useDeviceEditStripBox({ keyRecoil, isActive });

  return (
    <div className={classnames('edit-strip-box', className)}>
      {isLoadingStrip ? (
        <div className="edit-strip-box-skeleton">
          <Skeleton height="100%" />
        </div>
      ) : selectedData.id ? (
        <>
          <div className="edit-strip-top-content">
            {state.isDeviceEvent && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title">Event ID:</div>
                <div className="top-row-value">{eventId}</div>
                <IconButton
                  id="device-event-open-event-btn"
                  iconComponent={<img src={newTabIcon} alt="Close icon" />}
                  onClick={onClickNavigate}
                  iconText="Open"
                  isOpposite
                  isIconWithText
                  className="top-row-button"
                />
              </div>
            )}
            {!selectedData.isShowSymptoms && !state.isOtherEvent && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title">Event type:</div>
                <div className="top-row-value">{eventType}</div>
              </div>
            )}
            <div className="edit-strip-top-content__row">
              <div className="top-row-title">Trigger time:</div>
              <div className="top-row-value">{triggerTime}</div>
            </div>
            {state.isDeviceEvent && selectedData.isShowSymptoms && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title">Symptoms:</div>
                <div className="top-row-value">{symptoms || 'NA'}</div>
              </div>
            )}
            {state.type === TYPE_EVENT_OTHER && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title title-with-input">Title:</div>
                <TextInput
                  className="input-title-item"
                  name="title"
                  label=""
                  disabled={state.isRemoved || isNoEcgData}
                  value={state.title}
                  onChange={onChange}
                  isError={!!state.titleError}
                  errorMessage={state.titleError}
                  placeholder="Enter a title"
                  onKeyDown={onKeyDownInput}
                  maxLength={100}
                  id="device-edit-title-input"
                />
                {selectedData?.title !== state.title && !isDisabled && (
                  <div className="btm-btn-container fadeIn animated faster">
                    <IconButton
                      id="strips-device-cancel-title-btn"
                      isOutline
                      iconComponent={<img src={closeIcon} alt="Close icon" />}
                      onClick={() => {
                        onClickCancelChange('title');
                      }}
                    />
                    <IconButton
                      id="strips-device-apply-title-btn"
                      isFilled
                      iconComponent={<img src={greenCheckIcon} alt="Close icon" />}
                      onClick={() => {
                        onClickApplyChange('title');
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="divider" />
          <div className="edit-strip-bottom-content">
            <div className="edit-strip-bottom-content__row">
              <div className="btm-row-title">Technician comment:</div>
              {selectedData?.comment !== state.technicianComment && !isDisabled && (
                <div className="btm-btn-container fadeIn animated faster">
                  <IconButton
                    id="strips-device-cancel-comment-btn"
                    isOutline
                    iconComponent={<img src={closeIcon} alt="Close icon" />}
                    onClick={onClickCancelChange}
                  />
                  <IconButton
                    id="strips-device-apply-comment-btn"
                    isFilled
                    iconComponent={<img src={greenCheckIcon} alt="Close icon" />}
                    onClick={onClickApplyChange}
                  />
                </div>
              )}
            </div>
            <SuggestInput
              className="leave-comment-textarea"
              name="technicianComment"
              type="textarea"
              label=""
              disabled={state.isRemoved || isNoEcgData}
              value={state.technicianComment}
              onChange={onChange}
              isError={!!state.commentError}
              errorMessage={state.commentError}
              placeholder="Enter comment..."
              onKeyDown={onKeyDownInput}
              maxLength={selectedData?.isNewEventReport ? 720 : 520}
              id="deivce-edit-technician-comment-input"
              suggestTexts={SUGGEST_COMMENT}
              isReverse
            />
            {isNoEcgData && (
              <UncontrolledTooltip
                className="custom-tooltip"
                target="deivce-edit-technician-comment-input"
                placement="top"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                Cannot edit events with unavailable strips
              </UncontrolledTooltip>
            )}
          </div>
        </>
      ) : (
        <div className="no-data-display-container">There is no data to display</div>
      )}
    </div>
  );
};

export default DeviceEditStripBox;
